import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Building2, 
  FileText, 
  Shield, 
  TrendingUp, 
  CheckCircle2, 
  ArrowRight,
  CreditCard,
  Download,
  BarChart3,
  Clock,
  Loader2,
  ArrowLeft
} from 'lucide-react';

const SponsorshipPage = () => {
  const navigate = useNavigate();
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    name: '',
    email: ''
  });

  const handlePurchase = async () => {
    if (!purchaseForm.name || !purchaseForm.email) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await fetch(`${API}/sponsorship/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: purchaseForm.name,
          email: purchaseForm.email,
          success_url: `${origin}/sponsorship/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/sponsorship`
        })
      });

      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create checkout");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Building2,
      title: "Institutional Documentation",
      description: "Professional-grade documentation formatted for banks, CDFIs, and financial institutions."
    },
    {
      icon: Shield,
      title: "Risk Mitigation Framework",
      description: "Standardized risk mitigation language that positions your engagement as a monitored investment."
    },
    {
      icon: BarChart3,
      title: "ROI Projections",
      description: "Data-driven revenue projections based on your business metrics and growth goals."
    },
    {
      icon: FileText,
      title: "Sponsorship Letter Template",
      description: "Auto-generated formal request letter ready for institutional submission."
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Purchase Packet",
      description: "Secure your Funding Readiness Packet for $395"
    },
    {
      step: 2,
      title: "Complete Intake",
      description: "Provide business details and select your intended engagement tier"
    },
    {
      step: 3,
      title: "Generate Documentation",
      description: "Download your institutional-grade sponsorship packet"
    },
    {
      step: 4,
      title: "Secure Funding",
      description: "Submit to financial institutions for sponsorship support"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/mbc-logo.png" 
                alt="My Biz Consulting" 
                className="h-10 w-auto"
              />
              <span className="text-white font-semibold hidden sm:inline">Business Solutions Center</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/pricing" className="text-neutral-400 hover:text-white text-sm transition-colors">
                View Pricing
              </Link>
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                  Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-6">
            <Building2 className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">Institutional-Grade Documentation</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Institutional Sponsorship &<br />
            <span className="text-[#39FF14]">Funding Readiness Gateway</span>
          </h1>
          
          <p className="text-lg text-neutral-400 max-w-3xl mx-auto mb-8">
            Secure funding from banks, CDFIs, and financial institutions before enrolling in our engagement programs. 
            Professional documentation designed to support your sponsorship requests.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button 
              size="lg"
              onClick={() => setShowPurchaseDialog(true)}
              className="bg-[#39FF14] text-black hover:bg-[#32e612] font-semibold px-8"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Purchase Packet – $395
            </Button>
            <div className="text-neutral-500 text-sm">
              Includes $200 credit toward engagement enrollment
            </div>
          </div>

          {/* Value Props */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="bg-white/5 border-white/10 text-left">
                  <CardContent className="p-5">
                    <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm text-neutral-400">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-neutral-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white text-center mb-12">How It Works</h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="bg-neutral-800 rounded-xl p-6 h-full">
                  <div className="w-10 h-10 bg-[#39FF14] rounded-full flex items-center justify-center text-black font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-neutral-400">{step.description}</p>
                </div>
                {idx < processSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 w-6 h-6 text-neutral-600 transform -translate-y-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Tiers Preview */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white text-center mb-4">Select Your Intended Engagement</h2>
          <p className="text-neutral-400 text-center mb-12 max-w-2xl mx-auto">
            Choose the program you intend to enroll in after securing funding. This determines how your sponsorship packet is framed.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* 90-Day Tier */}
            <Card className="bg-white/5 border-white/10 hover:border-emerald-500/30 transition-colors">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  90-Day Engagement
                  <span className="text-[#39FF14] text-2xl font-bold">$1,500</span>
                </CardTitle>
                <CardDescription className="text-neutral-400">
                  Stabilization & structured implementation support
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-neutral-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    3 milestone checkpoints
                  </li>
                  <li className="flex items-start gap-2 text-neutral-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    Standard reporting framework
                  </li>
                  <li className="flex items-start gap-2 text-neutral-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    Consultant-led guidance
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* 6-Month Tier */}
            <Card className="bg-white/5 border-emerald-500/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-emerald-500 text-black text-xs font-bold px-3 py-1">
                RECOMMENDED
              </div>
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  6-Month Engagement
                  <span className="text-[#39FF14] text-2xl font-bold">$2,500</span>
                </CardTitle>
                <CardDescription className="text-neutral-400">
                  Extended performance oversight
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-neutral-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    6 milestone checkpoints
                  </li>
                  <li className="flex items-start gap-2 text-neutral-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    Enhanced reporting for institutions
                  </li>
                  <li className="flex items-start gap-2 text-neutral-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    Priority consultant access
                  </li>
                  <li className="flex items-start gap-2 text-neutral-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    Stronger institutional positioning
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-900/20 to-emerald-800/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Ready to Secure Your Funding?
          </h2>
          <p className="text-neutral-400 mb-8">
            Purchase your Institutional Sponsorship Packet today and take the first step toward funded business development.
          </p>
          <Button 
            size="lg"
            onClick={() => setShowPurchaseDialog(true)}
            className="bg-[#39FF14] text-black hover:bg-[#32e612] font-semibold px-8"
          >
            Get Started – $395
          </Button>
          <p className="text-neutral-500 text-sm mt-4">
            $200 credit applied when you enroll in an engagement program
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/mbc-logo.png" 
                alt="My Biz Consulting" 
                className="h-8 w-auto"
              />
              <span className="text-neutral-500 text-sm">Business Solutions Center</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-neutral-500">
              <Link to="/sponsorship/track" className="hover:text-white transition-colors">Verify Packet</Link>
              <Link to="/security" className="hover:text-white transition-colors">Security</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Purchase Sponsorship Packet
            </DialogTitle>
            <DialogDescription>
              Enter your details to proceed to secure checkout
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-emerald-900">Funding Readiness Packet</span>
                <span className="font-bold text-emerald-700">$395</span>
              </div>
              <p className="text-sm text-emerald-700 mt-1">
                Includes $200 enrollment credit
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={purchaseForm.name}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your full name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={purchaseForm.email}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="mt-1"
                />
              </div>
            </div>

            <Button
              onClick={handlePurchase}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Proceed to Checkout
                </>
              )}
            </Button>

            <p className="text-xs text-neutral-500 text-center">
              Secure payment powered by Stripe
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SponsorshipPage;
